import React, { useMemo, memo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import useStyle from './EmailSubjectAndBodyInputStyle';
import UtilsService from '../../../services/UtilsService';
import TemplateTextToolbar from './TemplateTextToolbar';
import TextEditor from '../../../widgets/rich-text-editor/TextEditorV2';
import createLinkPlugin from '../../../widgets/rich-text-editor/plugins/link/LinkPlugin';
import createTemplateTextPlugin from './template-text-plugin/TemplateTextPlugin';

// Modified by AI on 05/26/2026. Edit #1.
const EmailSubjectAndBodyInputComponent = ({
    className = '',
    disableLink = true,
    subjectClassName = '',
    bodyClassName = '',
    emailContextData = {},
    emailSubject = '',
    emailBody = '',
    emailSubjectError = false,
    emailBodyError = false,
    showSubject = true,
    showBody = true,
    onEmailSubjectChange = () => {},
    onEmailBodyChange = () => {},
    appEventForExternalSubjectChange = null,
    appEventForExternalBodyChange = null,
    type,
    readOnly,
    isActionButtonDisabled,
    campaignSenderType,
}) => {
    const { t } = useTranslation();
    const classes = useStyle({
        showSubject,
        showBody,
        emailSubjectError,
        emailBodyError,
    });
    const rootClasses = UtilsService.getClassName([
        className,
        classes.root,
    ]);
    const subjectTextEditorClasses = UtilsService.getClassName([
        subjectClassName,
        classes.subjectTextEditor,
    ]);
    const bodyTextEditorClasses = UtilsService.getClassName([
        bodyClassName,
        classes.bodyTextEditor,
    ]);

    const [activeEditorData, setActiveEditorData] = useState({});
    const [isHeaderFocused, setIsHeaderFocused] = useState(false);

    const subjectPlugins = useMemo(() => {
        const linkPlugin = createLinkPlugin({ disableLink, context: emailContextData });
        const templateTextPlugin = createTemplateTextPlugin({ templateTextContextData: emailContextData }, true);

        return [linkPlugin, templateTextPlugin];
    }, [disableLink, emailContextData]);

    const bodyPlugins = useMemo(() => {
        const linkPlugin = createLinkPlugin({ disableLink, context: emailContextData });
        const templateTextPlugin = createTemplateTextPlugin({ templateTextContextData: emailContextData, isActionButtonDisabled });

        return [linkPlugin, templateTextPlugin];
    }, [disableLink, emailContextData]);

    return (
        <div className={rootClasses}>
            { showSubject && <TextEditor
                disableFocus
                className={subjectTextEditorClasses}
                lines={2}
                readOnly={readOnly}
                lineHeight={24}
                value={emailSubject}
                placeHolder={t('CONTACT_CANDIDATE_EMAIL_FORM_SUBJECT_PLACEHOLDER')}
                onChange={onEmailSubjectChange}
                plugins={subjectPlugins}
                settings={{
                    showLabel: false,
                    showToolBar: false,
                    resizable: false,
                }}
                appEventForExternalValueChange={appEventForExternalSubjectChange}
                onFocus={(editorState, setEditorState, onChangeInternal) => {
                    setActiveEditorData({
                        editorState, setEditorState, onChangeInternal, type: 'emailSubject',
                    });
                    setIsHeaderFocused(false);
                }}
                onBlur={() => {
                    setIsHeaderFocused(true);
                }}
            /> }
            { showBody && <TextEditor
                fullHeight
                disableFocus
                readOnly={readOnly}
                className={bodyTextEditorClasses}
                value={emailBody}
                placeHolder={t('CONTACT_CANDIDATE_EMAIL_FORM_BODY_PLACEHOLDER')}
                onChange={onEmailBodyChange}
                plugins={bodyPlugins}
                appEventForExternalValueChange={appEventForExternalBodyChange}
                onFocus={(editorState, setEditorState, onChangeInternal) => setActiveEditorData({
                    editorState, setEditorState, onChangeInternal, type: 'emailBody',
                })}
                settings={{
                    showLabel: false,
                    showToolBar: true,
                    toolBarAlignment: 'bottom',
                    renderToolBar: (toolbarProps) => <TemplateTextToolbar
                        {...toolbarProps}
                        isHeaderFocused={isHeaderFocused}
                        type={activeEditorData.type}
                        isFeedbackActionShown={type !== 'linkedin'}
                        editorState={activeEditorData.editorState || toolbarProps.editorState}
                        setEditorState={activeEditorData.setEditorState || toolbarProps.setEditorState}
                        onChangeInternal={activeEditorData.onChangeInternal || toolbarProps.onChangeInternal}
                        campaignSenderType={campaignSenderType}
                    />,
                    resizable: false,
                }}
            /> }
        </div>
    );
};

EmailSubjectAndBodyInputComponent.propTypes = {
    className: PropTypes.string,
    disableLink: PropTypes.bool,
    emailSubject: PropTypes.string,
    emailBody: PropTypes.string,
    emailSubjectError: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    emailBodyError: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    showSubject: PropTypes.bool,
    showBody: PropTypes.bool,
    onEmailSubjectChange: PropTypes.func,
    onEmailBodyChange: PropTypes.func,
    appEventForExternalSubjectChange: PropTypes.string,
    appEventForExternalBodyChange: PropTypes.string,
    subjectClassName: PropTypes.string,
    bodyClassName: PropTypes.string,
    emailContextData: PropTypes.object,
    type: PropTypes.string,
    readOnly: PropTypes.bool,
    isActionButtonDisabled: PropTypes.bool,
    campaignSenderType: PropTypes.string,
};

export default memo(EmailSubjectAndBodyInputComponent);
